import "./App.css";
import { ToastContainer } from "react-toastify";
import { BrowserRouter } from "react-router-dom";
// import { initializeNotifications, onForegroundMessage } from './services/notificationService';
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useCallback } from "react";
import { addRecentLocation, setDetecting, setLocation } from "./Redux/Slices/LocationSlice";
import { reverseGeocode } from "./utils/geocoding";
import PWAInstall from "./Components/PWAInstall/PWAInstall";
import AuthInitializer from "./Common/AuthInitializer";
import { useDevice } from "./context";
import RouteIndex from "./Routes/RouteIndex";
// ⛔ DISABLED — Custom mobile push toast UI was not rendering correctly in production.
// Re-enable once the component is stabilised. Native OS/OneSignal notifications still work.
// import { PushNotificationToastContainer } from "./Components/Common/Notification/PushNotificationToast";
import { fetchUnreadCount } from "./Redux/Slices/Common/Notifications/NotificationsSlice";
import { showAlert } from "./Redux/Slices/Pages/Client/Ui/Ui";
import { FeedbackToastContainer } from "./Components/Common/FeedbackToast/FeedbackToast";
import { selectFeedbackQueue, showFeedback } from "./Redux/Slices/Common/Feedback/FeedbackSlice";

import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";


// ── Export so LocationPicker can use same function ─────────────────────────────
export const detectGPSLocation = (
  onSuccess,
  onError = () => { },
) => {
  if (!('geolocation' in navigator)) {
    onError({ code: 0, message: 'Geolocation not supported' });
    return;
  }

  // ✅ enableHighAccuracy: true → forces OS-level GPS permission dialog
  navigator.geolocation.getCurrentPosition(
    onSuccess,
    onError,
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    }
  );
};


// ── Shared location handler — used by both App + LocationPicker ────────────────
export const useLocationDetector = () => {
  const dispatch = useDispatch();

  // ✅ useCallback: stable reference across renders — stops downstream effects re-firing
  const detect = useCallback(async (coords) => {
    dispatch(setDetecting(true));
    try {
      const { latitude, longitude } = coords;
      const addressInfo = await reverseGeocode(latitude, longitude);
      const locationData = {
        ...addressInfo,
        latitude,
        longitude,
        detectedAt: new Date().toISOString(),
      };
      dispatch(setLocation(locationData));
      dispatch(addRecentLocation(locationData));
      return locationData;
    } catch (err) {
      
      return null;
    } finally {
      dispatch(setDetecting(false));
    }
  }, [dispatch]);

  return { detect };
};

// ══════════════════════════════════════════════════════════════════════════════

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}


// ── AppContent — inside BrowserRouter so hooks work correctly ──────────────────
function AppContent() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.login?.isAuthenticated);
  const { isMobile: mobileView, isTWA } = useDevice();
  const feedbackQueue = useSelector(selectFeedbackQueue);


  // ✅ Auto GPS removed for Play Store compliance
  // Location is now ONLY requested manually by the user

  // ── Intercept Referral Code Globally ─────────────────────────────────────────
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('bz_referral_code', refCode);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let oneSignalRef = null;
    const notificationHandler = (event) => {
      // Refresh counts
      dispatch(fetchUnreadCount('client'));
      dispatch(fetchUnreadCount('provider'));
    };

    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push((OneSignal) => {
      oneSignalRef = OneSignal;
      OneSignal.Notifications.addEventListener("foregroundWillDisplay", notificationHandler);
    });

    return () => {
      if (oneSignalRef) {
        oneSignalRef.Notifications.removeEventListener("foregroundWillDisplay", notificationHandler);
      }
    };
  }, [isAuthenticated, dispatch]);
//testing  


  // ── Mobile Alert Listener (Decoupled from Toast.jsx) ────────────────────────
  useEffect(() => {
    const handleMobileAlert = (e) => {
      dispatch(showAlert(e.detail));
    };
    window.addEventListener('BZ_SHOW_MOBILE_ALERT', handleMobileAlert);
    
    const handleFeedback = (e) => {
      dispatch(showFeedback(e.detail));
    };
    window.addEventListener('BZ_SHOW_FEEDBACK', handleFeedback);

    return () => {
      window.removeEventListener('BZ_SHOW_MOBILE_ALERT', handleMobileAlert);
      window.removeEventListener('BZ_SHOW_FEEDBACK', handleFeedback);
    };
  }, [dispatch]);

  return (
    <>
      <AuthInitializer />
      {!isTWA && <PWAInstall />}
      <RouteIndex />

      <FeedbackToastContainer queue={feedbackQueue} />

      {/* ⛔ DISABLED — PushNotificationToastContainer not working correctly in production. */}

      {!mobileView && (
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          className="custom-toast-container"
        />
      )}
    </>
  );
}

export default App;
